import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"
import { useReplaceBootScreen } from "@/lib/boot-screen"

/**
 * The frame of every auth screen: one card, the screen's icon, its `h1` and
 * its line of explanation, then the form. The heading is the sign-in screen's
 * largest paint (NFR-DSH-01): index.html paints this same markup before the
 * bundle runs (DW-61), so keep it free of anything that depends on data.
 */
export function AuthCard({
  title,
  description,
  icon: Icon,
  children,
  footer,
}: {
  title: string
  description?: string | undefined
  /** What the screen is for, drawn in the brand's colours above the heading. */
  icon?: LucideIcon | undefined
  children: ReactNode
  footer?: ReactNode
}) {
  // The real screen replaces index.html's static copy in its own commit (DW-61).
  useReplaceBootScreen()
  return (
    <>
      <section className="auth-card rounded-2xl border border-border p-6 text-card-foreground sm:p-8">
        <header className="mb-6 space-y-2 text-center">
          {Icon ? (
            <span className="auth-badge mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl">
              <Icon aria-hidden="true" className="size-6" />
            </span>
          ) : null}
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </header>
        {children}
      </section>
      {footer ? (
        <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>
      ) : null}
    </>
  )
}
